import { useMemo } from 'react';
import type { OptionBuyingStrategy } from '@einhaus/project314-types';
import { useOptionBuyingStrategies } from '@/hooks/useOptionBuyingStrategies';
import {
    formatOptionBuyingMethod,
    formatOptionBuyingStrategyMeta,
    formatOptionBuyingStrategyName
} from '@utils/optionBuyingStrategyHelpers';
import SearchSelect, { type SearchSelectFormProps, type SearchSelectItem } from './SearchSelect';

interface CommonProps extends SearchSelectFormProps {
    /** Restricts the result list to an already-defined set, such as a trading profile's effective strategies. */
    allowedIds?: number[];
    noOptionsMessage?: string;
}

interface SingleProps extends CommonProps {
    mode: 'single';
    value: number | null;
    onChange: (id: number | null) => void;
}

interface MultiProps extends CommonProps {
    mode: 'multi';
    value: number[];
    onChange: (ids: number[]) => void;
}

export type OptionBuyingStrategySelectProps = SingleProps | MultiProps;

const toItem = (strategy: OptionBuyingStrategy): SearchSelectItem => {
    const name = formatOptionBuyingStrategyName(strategy);

    return {
        badge: strategy.active ? undefined : 'Inactive',
        label: `${name} (#${strategy.id})`,
        meta: formatOptionBuyingStrategyMeta(strategy),
        name,
        searchText: formatOptionBuyingMethod(strategy.buyingMethod).toLowerCase(),
        value: strategy.id
    };
};

const missingItem = (strategyId: number): SearchSelectItem => ({
    label: `Option Strategy ${strategyId} (#${strategyId})`,
    meta: 'Not in your current strategy list',
    name: `Option Strategy ${strategyId}`,
    value: strategyId
});

/**
 * Searchable single/multi select for option buying strategies. Fetches its own
 * data via useOptionBuyingStrategies, so call sites only manage the selected
 * id(s). Search matches name, id (with or without #), and buying method.
 */
const OptionBuyingStrategySelect = (props: OptionBuyingStrategySelectProps) => {
    const { error: strategiesError, isLoading, strategies } = useOptionBuyingStrategies();
    const { allowedIds, mode, value, onChange, ...formProps } = props;

    const items = useMemo(() => {
        if (!allowedIds) return strategies.map(toItem);

        const allowedIdSet = new Set(allowedIds);
        return strategies.filter((strategy) => allowedIdSet.has(strategy.id)).map(toItem);
    }, [allowedIds, strategies]);

    const loadError = strategiesError ? `Failed to load option buying strategies: ${strategiesError.message}` : undefined;
    const placeholder = props.placeholder ?? (mode === 'multi' ? 'Select option strategies...' : 'Select an option strategy...');

    const commonProps = {
        ...formProps,
        isLoading,
        items,
        loadError,
        placeholder,
        resolveMissingItem: missingItem
    };

    return mode === 'multi' ? (
        <SearchSelect {...commonProps} mode="multi" value={value} onChange={onChange} />
    ) : (
        <SearchSelect {...commonProps} mode="single" value={value} onChange={onChange} />
    );
};

export default OptionBuyingStrategySelect;
